import { useEffect, useState } from "react";
import { MapPin, Search, Loader2, Phone, Clock, Globe, FileDown, LocateFixed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { api, errMsg, downloadFile } from "@/lib/api";
import { toast } from "sonner";

export default function CafNearby() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [guides, setGuides] = useState([]);
  const [guide, setGuide] = useState("isee");
  useEffect(() => {
    api.get("/guides").then(({ data }) => setGuides(data));
  }, []);

  const search = async (params) => {
    setBusy(true);
    try {
      const { data } = await api.get("/caf/nearby", { params });
      setRes(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const locate = () => navigator.geolocation?.getCurrentPosition((p) => search({ lat: p.coords.latitude, lon: p.coords.longitude }), () => toast.error("Posizione non disponibile"));

  return (
    <div data-testid="caf-page">
      <PageHeader eyebrow="CAF e patronati" title="Trova un CAF vicino" desc="Cerca per città o CAP. Scarica la lista dei documenti da portare, così non devi tornare due volte." />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && q && search({ q })} placeholder="es. Torino oppure 10131" className="pl-9 h-11 bg-white" data-testid="caf-search-input" /></div>
            <Button onClick={() => search({ q })} disabled={busy || !q} className="h-11 rounded-full" data-testid="caf-search-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cerca"}</Button>
            <Button onClick={locate} variant="outline" className="h-11 rounded-full" data-testid="caf-locate-btn"><LocateFixed className="h-4 w-4 mr-2" /> Vicino a me</Button>
          </div>
          {res && res.results.length === 0 && <p className="rounded-3xl border bg-white p-8 text-slate-500" data-testid="caf-empty">Nessun CAF trovato nella mappa entro 6 km. Prova una città vicina.</p>}
          {res?.results.map((c, i) => (
            <div key={i} className="rounded-2xl border bg-white p-5 flex gap-4 lift" data-testid={`caf-item-${i}`}>
              <MapPin className="h-5 w-5 text-[#E07A5F] shrink-0 mt-1" />
              <div className="flex-1 min-w-0">
                <p className="font-medium">{c.name} <span className="text-xs text-slate-400">· {c.km} km</span></p>
                {c.address && <p className="text-sm text-slate-600">{c.address}</p>}
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                  {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1 text-[#1E3A8A]"><Phone className="h-3.5 w-3.5" />{c.phone}</a>}
                  {c.hours && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{c.hours}</span>}
                  {c.website?.startsWith("http") && <a href={c.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#1E3A8A]"><Globe className="h-3.5 w-3.5" />Sito</a>}
                  <a href={`https://www.openstreetmap.org/?mlat=${c.lat}&mlon=${c.lon}#map=18/${c.lat}/${c.lon}`} target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A]">Mappa</a>
                </div>
              </div>
            </div>
          ))}
          {res && <p className="text-xs text-slate-500">Dati da OpenStreetMap: possono essere incompleti. Chiama prima per orari e appuntamento.</p>}
        </div>
        <div className="rounded-3xl border bg-white p-6 h-fit space-y-3">
          <h3 className="font-medium">Documenti da portare</h3>
          <Select value={guide} onValueChange={setGuide}>
            <SelectTrigger data-testid="caf-guide-select"><SelectValue /></SelectTrigger>
            <SelectContent>{guides.map((g) => <SelectItem key={g.slug} value={g.slug}>{g.title}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" className="w-full rounded-full" onClick={() => downloadFile(`/guides/${guide}/checklist.pdf`, `checklist-${guide}.pdf`)} data-testid="caf-checklist-btn"><FileDown className="h-4 w-4 mr-2" /> Scarica la checklist PDF</Button>
        </div>
      </div>
    </div>
  );
}
